const API_BASE = '/api';

const getToken = () => localStorage.getItem('sss_crm_token');
const getViewAs = () => {
  const v = localStorage.getItem('sss_crm_view_as');
  return v && /^\d+$/.test(v) ? v : null;
};
export const setViewAs = (userId) => {
  if (userId) localStorage.setItem('sss_crm_view_as', String(userId));
  else localStorage.removeItem('sss_crm_view_as');
};
export const getViewAsId = () => getViewAs();

const request = async (path, options = {}) => {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Impersonation: append ?_as=<id> to every request when admin has chosen
  // to preview as another user. Server validates that the JWT belongs to an
  // admin/leadership user before honoring the swap.
  const viewAs = getViewAs();
  let url = `${API_BASE}${path}`;
  if (viewAs) url += (url.includes('?') ? '&' : '?') + '_as=' + viewAs;

  const res = await fetch(url, { ...options, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    // status + the parsed body ride along for callers that need more than the
    // text (HASC: a 502 that left the row Unconfirmed MAY be booked).
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const api = {
  // auth
  // `website` is the login honeypot field (server/utils/loginGuard.js) — a
  // person never sees it, so it is always '' from the real form.
  login: (username, password, website = '') => request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password, website }) }),
  me: () => request('/auth/me'),
  // A password change retires every token issued before it (the one that made
  // this request included); the server hands back a fresh one, stored here so
  // EVERY caller keeps its session without each page remembering to.
  changePassword: (current_password, new_password) =>
    request('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password, new_password }) })
      .then(d => { if (d?.token) setToken(d.token); return d; }),
  initialPassword: (new_password) =>
    request('/auth/initial-password', { method: 'POST', body: JSON.stringify({ new_password }) })
      .then(d => { if (d?.token) setToken(d.token); return d; }),
  // The rep's own phone. This is the number the customer reads on the quote's
  // Contact line, so it is theirs to set — no admin round trip.
  updateMyPhone: (phone) =>
    request('/auth/me/phone', { method: 'PUT', body: JSON.stringify({ phone }) }),
  forgotPassword: (username, website = '') =>
    request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ username, website }) }),
  resetPassword: (token, new_password) =>
    request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, new_password }) }),

  // lookups (any authed user)
  listBranches: () => request('/lookups/branches'),
  listReps: () => request('/lookups/reps'),
  listActiveUsers: () => request('/lookups/active-users'),
  lookupCrafts: () => request('/lookups/crafts'),

  // admin
  getSettings: () => request('/admin/settings'),
  updateSetting: (key, value) => request(`/admin/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  // Returns { last_rentalman_upload, last_users_import, last_projects_import,
  //           last_call_log_import, last_customers_import, ... }
  // Each value is { at, user_id, user_name, count, extra } | null.
  getUploadStatus: () => request('/admin/upload-status'),
  listAllBranches: () => request('/admin/branches'),
  createBranch: (b) => request('/admin/branches', { method: 'POST', body: JSON.stringify(b) }),
  updateBranch: (id, b) => request(`/admin/branches/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
  // Cleanup tools (admin)
  findCallLogDuplicates: () => request('/admin/duplicates/call-log'),
  cleanupCallLogDuplicates: (ids) => request('/admin/duplicates/call-log/cleanup', { method: 'POST', body: JSON.stringify({ ids }) }),
  // Backups (admin)
  backupStatus: () => request('/admin/backup/status'),
  runBackup: () => request('/admin/backup/run', { method: 'POST' }),
  listBackups: (limit) => request(`/admin/backup/list${limit ? '?limit=' + limit : ''}`),
  disaSyncStatus: () => request('/admin/disa-sync/status'),
  runDisaSync: () => request('/admin/disa-sync/run', { method: 'POST' }),
  isnSyncStatus: () => request('/admin/isn-sync/status'),
  runIsnSync: () => request('/admin/isn-sync/run', { method: 'POST' }),
  // RentalMan "All Invoices" feed -> opportunity Actual $ (docs/SPEC-rm-invoiced-actuals.md).
  rmInvoiceSyncStatus: () => request('/admin/rm-invoice-sync/status'),
  runRmInvoiceSync: () => request('/admin/rm-invoice-sync/run', { method: 'POST' }),
  hascSyncStatus: () => request('/admin/hasc-sync/status'),
  runHascSync: (start_date, end_date) => request('/admin/hasc-sync/run', {
    method: 'POST', body: JSON.stringify({ start_date: start_date || undefined, end_date: end_date || undefined }),
  }),
  // HASC Phase 2, "Register with HASC" (server/routes/hasc.js). REAL MONEY:
  // hascRegister and hascRegisterBulk create BILLABLE HASC registrations, so
  // only a confirmed click in the in-page panels calls them. Everything else
  // is a $0 read or a write to the CRM's own table.
  hascRegistrationStatus: () => request('/hasc/registration/status'),
  // courseCode may carry "/" alternates exactly as typed on the requirement.
  hascRegistrationOptions: (employeeId, courseCode, projectId) => request('/hasc/registration/options?employee_id=' + employeeId
    + '&course_code=' + encodeURIComponent(courseCode) + (projectId ? '&project_id=' + projectId : '')),
  hascJobPlan: (projectId, account) => request('/hasc/registration/job-plan?project_id=' + projectId
    + (account ? '&account=' + encodeURIComponent(account) : '')),
  // Admin: every location of every configured account with its course count ($0).
  hascCatalog: () => request('/hasc/registration/catalog'),
  // { employee_id } | { project_id }; no filter = admin only, the 20 newest.
  hascRegistrations: ({ employee_id, project_id, limit } = {}) => {
    const qs = new URLSearchParams();
    if (employee_id) qs.set('employee_id', employee_id);
    if (project_id) qs.set('project_id', project_id);
    if (limit) qs.set('limit', limit);
    const s = qs.toString();
    return request('/hasc/registrations' + (s ? '?' + s : ''));
  },
  hascRegister: (payload) => request('/hasc/registrations', { method: 'POST', body: JSON.stringify(payload) }),
  // { project_id, account, po_number, pfi, desired_date, items: [...] }, at most bulk_max items per call.
  hascRegisterBulk: (payload) => request('/hasc/registrations/bulk', { method: 'POST', body: JSON.stringify(payload) }),
  hascRefreshRegistration: (id) => request(`/hasc/registrations/${id}/refresh`, { method: 'POST' }),
  hascCancelRegistration: (id) => request(`/hasc/registrations/${id}/cancel`, { method: 'POST' }),
  disaReportStatus: () => request('/workforce/disa/sync-status'),
  employeeRecords: (id) => request('/workforce/employees/' + id + '/records'),
  // through = the optional "Ready through" date (YYYY-MM-DD) for the no-job view.
  siteAccess: (customerId, siteId, projectId, through) => request('/workforce/site-access?customer_id=' + customerId + (siteId ? '&site_id=' + siteId : '') + (projectId ? '&project_id=' + projectId : '') + (through ? '&through=' + encodeURIComponent(through) : '')),
  siteAccessJobs: () => request('/workforce/site-access/jobs'),
  siteAccessUnevaluableJobs: () => request('/workforce/site-access/unevaluable-jobs'),
  // Person-first: one employee against every customer / site row ("Cleared for").
  employeeSiteAccess: (id, through) => request('/workforce/employees/' + id + '/site-access' + (through ? '?through=' + encodeURIComponent(through) : '')),
  opsDataFeeds: () => request('/operations/data-feeds'),
  rmInvoiceFeed: () => request('/projects/rm-invoice-feed'),
  siteAccessEmail: (payload) => request('/workforce/site-access/email', { method: 'POST', body: JSON.stringify(payload) }),
  // Preview: the same email, built but not sent or logged (no SMTP needed).
  siteAccessEmailPreview: (payload) => request('/workforce/site-access/email?dry=true', { method: 'POST', body: JSON.stringify(payload) }),
  syncOpsCrew: (estimateId, body) => request(`/estimates/${estimateId}/sync-allocations`, { method: 'POST', body: JSON.stringify(body || {}) }),
  // Payroll paperwork (2026-07-17, replaces the standalone Payroll app):
  getPersonnelReport: (estimateId) => request(`/estimates/${estimateId}/personnel-report`),
  sendPersonnelReport: (estimateId, payload) => request(`/estimates/${estimateId}/personnel-report/send`, { method: 'POST', body: JSON.stringify(payload) }),
  // Returns a fetch Response whose body is the exact PDF the send would email.
  previewPersonnelReport: (estimateId, payload) =>
    fetch(`${API_BASE}/estimates/${estimateId}/personnel-report/preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  listUsers: () => request('/admin/users'),
  createUser: (u) => request('/admin/users', { method: 'POST', body: JSON.stringify(u) }),
  updateUser: (id, u) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(u) })
    .then(d => { if (d?.token) setToken(d.token); return d; }), // own-password reset rotates the session
  deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  sendWelcomeEmail: (id, temp_password) =>
    request(`/admin/users/${id}/welcome`, { method: 'POST', body: JSON.stringify({ temp_password }) }),

  // call log
  listCallLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/call-log${q ? '?' + q : ''}`);
  },
  // xlsx blob download. Admin-only on the server.
  exportCallLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/call-log/export.xlsx${q ? '?' + q : ''}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
  },
  // Opportunity picker for an activity: that customer's projects, role-scoped.
  activityOpportunities: (customer) =>
    request(`/call-log/opportunities?customer=${encodeURIComponent(customer || '')}`),
  createCallLog: (e) => request('/call-log', { method: 'POST', body: JSON.stringify(e) }),
  updateCallLog: (id, e) => request(`/call-log/${id}`, { method: 'PUT', body: JSON.stringify(e) }),
  flagCallLog: (id, flag) => request(`/call-log/${id}/flag`, { method: 'POST', body: JSON.stringify({ flag }) }),
  addCallLogFollowUp: (id, body) => request(`/call-log/${id}/follow-up`, { method: 'POST', body: JSON.stringify(body) }),
  deleteCallLog: (id) => request(`/call-log/${id}`, { method: 'DELETE' }),

  // projects
  listProjects: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/projects${q ? '?' + q : ''}`);
  },
  listProjectEvents: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/projects/events${q ? '?' + q : ''}`);
  },
  // Returns a fetch Response with the xlsx blob; caller turns it into a
  // download. Includes the auth header so an admin token is sent.
  exportProjects: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/projects/export.xlsx${q ? '?' + q : ''}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
  },
  getProject: (id) => request(`/projects/${id}`),
  createProject: (p) => request('/projects', { method: 'POST', body: JSON.stringify(p) }),
  updateProject: (id, p) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(p) }),
  deleteProject: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
  // The RentalMan invoices behind an opportunity's Actual $ (sales scope).
  projectRmInvoices: (id) => request(`/projects/${id}/rm-invoices`),

  // dashboard
  kpis: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/dashboard/kpis${q ? '?' + q : ''}`);
  },
  byRep: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/dashboard/by-rep${q ? '?' + q : ''}`);
  },
  // Sales Revenue tile drill-down: customer rollup + raw invoice rows.
  // params: { kind: 'Base'|'TA'|'All', year, branch_id, owner_id, limit }
  billedDrill: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/dashboard/billed-drill${q ? '?' + q : ''}`);
  },

  // operations — read-only Requests & Estimates viewer (bill-side only)
  opsSalesRequests: (status) => request('/ops-sales/requests' + (status ? '?status=' + encodeURIComponent(status) : '')),
  opsSalesRequest: (id) => request(`/ops-sales/requests/${id}`),
  opsSalesEstimates: () => request('/ops-sales/estimates'),
  opsSalesEstimate: (id) => request(`/ops-sales/estimates/${id}`),

  // operations
  opsFeed: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/operations/feed${q ? '?' + q : ''}`);
  },
  opsUpdateStatus: (id, status) =>
    request(`/operations/projects/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  opsGetProject: (id) => request(`/operations/projects/${id}`),
  opsJobHub: (id) => request(`/operations/projects/${id}/hub`),
  // Same drill-down as projectRmInvoices, through the Ops router's scope.
  opsProjectRmInvoices: (id) => request(`/operations/projects/${id}/rm-invoices`),
  opsEmailJobHub: (id, to) =>
    request(`/operations/projects/${id}/email-pm`, { method: 'POST', body: JSON.stringify({ to }) }),
  // Job Hub Onsite block: "this FS person IS this roster employee" (2026-09-22).
  opsLinkOnsite: (id, fsRosterId, employeeId) =>
    request(`/operations/projects/${id}/onsite-links`, { method: 'POST', body: JSON.stringify({ fs_roster_id: fsRosterId, employee_id: employeeId }) }),
  jobCompliance: (projectId) => request(`/workforce/compliance/${projectId}`),
  // Set/clear the facility owner a job's compliance is judged against
  // (Custofab-at-CHS-Laurel). null clears back to the billing customer.
  opsSetFacility: (id, customer_id) => request(`/operations/projects/${id}/facility`, {
    method: 'PUT', body: JSON.stringify({ customer_id }),
  }),
  // Set/clear the job's site (one of the judged customer's customer_sites):
  // its site requirements row then governs Site Ready. null clears.
  opsSetJobSite: (id, site_id) => request(`/operations/projects/${id}/site`, {
    method: 'PUT', body: JSON.stringify({ site_id }),
  }),
  opsUpdateVendorPo: (id, data) =>
    request(`/operations/projects/${id}/vendor-po`, { method: 'PUT', body: JSON.stringify(data) }),
  opsListProjectFiles: (id) => request(`/operations/projects/${id}/files`),
  opsUploadProjectFile: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/operations/projects/${id}/files`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },
  opsDeleteProjectFile: (id, fileId) =>
    request(`/operations/projects/${id}/files/${fileId}`, { method: 'DELETE' }),
  opsCreateProject: (payload) =>
    request('/operations/projects', { method: 'POST', body: JSON.stringify(payload) }),
  opsUpdateJobNumber: (id, job_number) =>
    request(`/operations/projects/${id}/job-number`, { method: 'PUT', body: JSON.stringify({ job_number }) }),
  // TA job-number pool register for one year -- powers the Job #s tab
  // (every 801-820 slot, who holds it, collisions, what is free).
  opsJobNumberPool: (year) =>
    request(`/operations/job-numbers${year ? `?year=${encodeURIComponent(year)}` : ''}`),
  // Mint the next pool number onto a job. replace=true renumbers a job that
  // already has one -- the collision fix.
  opsGenerateJobNumber: (id, replace = false) =>
    request(`/operations/projects/${id}/job-number/generate`, { method: 'POST', body: JSON.stringify({ replace }) }),
  opsUpdatePlannedDate: (id, date_planned) =>
    request(`/operations/projects/${id}/planned-date`, { method: 'PUT', body: JSON.stringify({ date_planned }) }),
  opsUpdateDuration: (id, duration_days) =>
    request(`/operations/projects/${id}/duration`, { method: 'PUT', body: JSON.stringify({ duration_days }) }),
  opsUpdatePoNumber: (id, po_number) =>
    request(`/operations/projects/${id}/po-number`, { method: 'PUT', body: JSON.stringify({ po_number }) }),
  opsSetHidden: (id, ops_hidden) =>
    request(`/operations/projects/${id}/ops-hidden`, { method: 'PUT', body: JSON.stringify({ ops_hidden }) }),
  // Push/re-push one job to Field Services (auto-mirror covers ops edits;
  // this is the backfill + retry). Auto-mints a TA job number when blank.
  opsPushFS: (id) =>
    request(`/operations/projects/${id}/push-fs`, { method: 'POST' }),
  // Branch/TA-Div toggle — TRUE = billed at the branch counter; FS excludes
  // the job from its revenue forecast. Mirrors to FS automatically.
  opsSetBillingLevel: (id, branch_billed) =>
    request(`/operations/projects/${id}/billing-level`, { method: 'PUT', body: JSON.stringify({ branch_billed }) }),
  // Crew date sync after a job's Start / End moves. Preview first (dry), then
  // commit with the allocation_ids the user actually ticked.
  opsPlanCrewDates: (id, payload) =>
    request(`/operations/projects/${id}/crew-dates?dry=true`, { method: 'POST', body: JSON.stringify(payload) }),
  opsApplyCrewDates: (id, payload) =>
    request(`/operations/projects/${id}/crew-dates`, { method: 'POST', body: JSON.stringify(payload) }),

  // Operations -> Applicants (admin/leadership only, server-enforced)
  applicantsList: () => request('/applicants'),
  applicantUpdate: (id, payload) => request(`/applicants/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  applicantHire: (id) => request(`/applicants/${id}/hire`, { method: 'POST' }),
  applicantSync: () => request('/applicants/sync', { method: 'POST' }),
  applicantDownloadFile: async (id, idx, filename) => {
    const r = await fetch(`${API_BASE}/applicants/${id}/files/${idx}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'resume';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Operations -> DISA Randoms (admin/leadership only, server-enforced)
  disaRandomsList: (period) => request(`/disa-randoms${period ? `?period=${encodeURIComponent(period)}` : ''}`),
  disaRandomUpdate: (id, payload) => request(`/disa-randoms/selections/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  disaRandomsSync: () => request('/disa-randoms/sync', { method: 'POST' }),
  disaAnnualList: () => request('/disa-randoms/annual'),
  disaAnnualUpdate: (id, payload) => request(`/disa-randoms/annual/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  disaRosterChanges: () => request('/disa-randoms/roster-changes'),
  disaRosterChangeUpdate: (id, payload) => request(`/disa-randoms/roster-changes/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  disaRandomDownloadPdf: async (groupId, filename) => {
    const r = await fetch(`${API_BASE}/disa-randoms/groups/${groupId}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'DISA random report.pdf';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // ISNetworld client-level compliance
  isnList: () => request('/isn'),
  isnGrades: () => request('/isn/grades'),
  isnCreate: (payload) => request('/isn', { method: 'POST', body: JSON.stringify(payload) }),
  isnUpdate: (id, payload) => request(`/isn/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  isnDelete: (id) => request(`/isn/${id}`, { method: 'DELETE' }),

  // equipment gantt
  ganttFeed: () => request('/gantt/feed'),
  ganttStock: () => request('/gantt/stock'),
  ganttCandidates: (q = '') => request(`/gantt/candidates${q ? '?q=' + encodeURIComponent(q) : ''}`),
  ganttToggle: (id, on_gantt) =>
    request(`/gantt/projects/${id}/toggle`, { method: 'PUT', body: JSON.stringify({ on_gantt }) }),
  ganttUpdateProject: (id, fields) =>
    request(`/gantt/projects/${id}`, { method: 'PUT', body: JSON.stringify(fields) }),
  ganttCatalog: (q = '') => request(`/gantt/catalog${q ? '?q=' + encodeURIComponent(q) : ''}`),
  ganttAddEquipLine: (projectId, line) =>
    request(`/gantt/projects/${projectId}/equipment-lines`, { method: 'POST', body: JSON.stringify(line) }),
  ganttUpdateEquipLine: (lineId, line) =>
    request(`/gantt/equipment-lines/${lineId}`, { method: 'PUT', body: JSON.stringify(line) }),
  ganttDeleteEquipLine: (lineId) =>
    request(`/gantt/equipment-lines/${lineId}`, { method: 'DELETE' }),
  ganttAddLaborLine: (projectId, line) =>
    request(`/gantt/projects/${projectId}/labor-lines`, { method: 'POST', body: JSON.stringify(line) }),
  ganttUpdateLaborLine: (lineId, line) =>
    request(`/gantt/labor-lines/${lineId}`, { method: 'PUT', body: JSON.stringify(line) }),
  ganttDeleteLaborLine: (lineId) =>
    request(`/gantt/labor-lines/${lineId}`, { method: 'DELETE' }),
  ganttPush: (projectId) =>
    request(`/gantt/projects/${projectId}/push`, { method: 'POST' }),
  ganttAllocations: (projectId) =>
    request(`/gantt/allocations${projectId != null ? '?project_id=' + projectId : ''}`),
  ganttSaveAllocations: (projectId, alloc, thirdParty) =>
    request('/gantt/allocations', { method: 'PUT', body: JSON.stringify({ project_id: projectId ?? null, alloc, thirdParty: thirdParty || {} }) }),
  // Third-party vendor quote (upload / download / delete) per (scope, cat_class).
  ganttUploadTpQuote: (projectId, catClass, file) => {
    const fd = new FormData();
    fd.append('project_id', projectId ?? '');
    fd.append('cat_class', catClass);
    fd.append('file', file);
    return fetch(`${API_BASE}/gantt/allocations/quote`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; });
  },
  ganttDownloadTpQuote: async (projectId, catClass, filename) => {
    const qs = `project_id=${projectId ?? ''}&cat_class=${encodeURIComponent(catClass)}`;
    const r = await fetch(`${API_BASE}/gantt/allocations/quote/download?${qs}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'vendor-quote';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  ganttDeleteTpQuote: (projectId, catClass) =>
    request('/gantt/allocations/quote', { method: 'DELETE', body: JSON.stringify({ project_id: projectId ?? null, cat_class: catClass }) }),
  // xlsx blob downloads — caller turns the Response into a download
  ganttPunchlist: (projectId) =>
    fetch(`${API_BASE}/gantt/projects/${projectId}/punchlist.xlsx`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }),
  ganttPunchlistCsv: (projectId) =>
    fetch(`${API_BASE}/gantt/projects/${projectId}/punchlist.csv`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }),
  ganttTemplate: () =>
    fetch(`${API_BASE}/gantt/template.xlsx`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }),
  // Master Gantt roll-up export — scoped to the page's current Focus +
  // Include-scenarios selection so the download matches what's on screen.
  ganttMasterGantt: (focusId, includeScenarios) =>
    fetch(`${API_BASE}/gantt/master-gantt.xlsx?focus=${focusId ?? 'all'}&scenarios=${includeScenarios ? 'true' : 'false'}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }),
  ganttUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE}/gantt/upload`, { method: 'POST', headers, body: fd })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
        return data;
      });
  },

  // search
  search: (q) => request(`/search?q=${encodeURIComponent(q)}`),

  // calendar
  calendar: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/calendar${q ? '?' + q : ''}`);
  },

  // tasks
  listTasks: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/tasks${q ? '?' + q : ''}`);
  },
  createTask: (t) => request('/tasks', { method: 'POST', body: JSON.stringify(t) }),
  updateTask: (id, t) => request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(t) }),
  deleteTask: (id) => request(`/tasks/${id}`, { method: 'DELETE' }),

  // quotas
  // Performance Targets (replaces the older quota concept). 6-tier annual
  // ladder per user per year + per-period realized + commission earned.
  myPerformanceTargets: (year) => request(`/performance-targets/me${year ? '?year=' + year : ''}`),
  teamPaceBoard: (year) => request(`/performance-targets/team${year ? '?year=' + year : ''}`),
  amDrill: (id, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/dashboard/am/${id}${q ? '?' + q : ''}`);
  },
  sendUpcomingWork: (dry) => request(`/notifications/send-upcoming-work${dry ? '?dry=true' : ''}`, { method: 'POST' }),
  sendMobDemob: (dry) => request(`/notifications/send-mob-demob${dry ? '?dry=true' : ''}`, { method: 'POST' }),
  sendDailyJobs: (dry) => request(`/notifications/send-daily-jobs${dry ? '?dry=true' : ''}`, { method: 'POST' }),
  sendPoRedirect: (dry) => request(`/notifications/send-po-redirect${dry ? '?dry=true' : ''}`, { method: 'POST' }),
  sendVpScorecard: (dry) => request(`/notifications/send-vp-scorecard${dry ? '?dry=true' : ''}`, { method: 'POST' }),
  listManuals: () => request('/manuals'),
  getManual: (key) => request(`/manuals/${key}`),
  listPerformanceTargets: (year) => request(`/performance-targets${year ? '?year=' + year : ''}`),
  setPerformanceTargets: (userId, year, body) => request(`/performance-targets/${userId}/${year}`, { method: 'PUT', body: JSON.stringify(body) }),
  deletePerformanceTargets: (userId, year) => request(`/performance-targets/${userId}/${year}`, { method: 'DELETE' }),

  // Events (AM-scheduled luncheons / dinners / golf / trade shows / etc.)
  // with optional support attendees who Accept/Decline. Hidden from the
  // attendee's calendar until they Accept.
  listEvents: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/events${q ? '?' + q : ''}`);
  },
  listEventInvitations: () => request('/events/invitations'),
  getEvent: (id) => request(`/events/${id}`),
  createEvent: (body) => request('/events', { method: 'POST', body: JSON.stringify(body) }),
  updateEvent: (id, body) => request(`/events/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  cancelEvent: (id) => request(`/events/${id}`, { method: 'DELETE' }),
  respondToEvent: (id, response) => request(`/events/${id}/respond`, { method: 'POST', body: JSON.stringify({ response }) }),
  inviteToEvent: (id, userId) => request(`/events/${id}/invite`, { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
  removeEventInvite: (id, attendeeId) => request(`/events/${id}/invite/${attendeeId}`, { method: 'DELETE' }),

  // Per-rep monthly Sales Revenue (formerly Base Revenue). Admin can
  // enter manually OR upload a Rentalman invoice export. Each (rep,
  // branch, month) row has 4 values: rentals + labor + consumables
  // (drives commission) + unallocated (catch-all). Per-row branch_id
  // for accurate Branch Budget attribution.
  listSalesRevenue: (year) => request(`/sales-revenue${year ? '?year=' + year : ''}`),
  saveSalesRevenueBulk: (userId, year, months) =>
    request(`/sales-revenue/${userId}/${year}/bulk`, { method: 'PUT', body: JSON.stringify({ months }) }),
  // Adjustment = signed delta layered on top of an imported month (import
  // rows untouched; drill-down keeps reconciling). All-zero amounts remove it.
  saveSalesRevenueAdjustment: (userId, year, month, body) =>
    request(`/sales-revenue/${userId}/${year}/${month}/adjustment`, { method: 'PUT', body: JSON.stringify(body) }),
  // Override = hard replace of one user-month (all branches, Base + TA)
  // with a single manual row at the rep's home branch.
  overrideSalesRevenueMonth: (userId, year, month, body) =>
    request(`/sales-revenue/${userId}/${year}/${month}/override`, { method: 'PUT', body: JSON.stringify(body) }),
  // Upload returns a preview; admin reviews then calls commit to actually
  // write. Replace mode: covers months wipe all rows for those months.
  uploadSalesRevenue: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const viewAs = getViewAs();
    const url = '/api/sales-revenue/upload' + (viewAs ? '?_as=' + viewAs : '');
    return fetch(url, { method: 'POST', headers, body: fd })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        return data;
      });
  },
  commitSalesRevenueUpload: (aggregated, replaceMonths, raw) =>
    request('/sales-revenue/commit-upload', {
      method: 'POST',
      body: JSON.stringify({ aggregated, replace_months: replaceMonths, raw: raw || [] })
    }),

  // commissions
  // Earned commission per rep -- each rentalman_invoices line's paid status
  // from the RentalMan All Invoices feed (rm_all_invoices), matched on account,
  // invoice # and sequence. Rep gets paid only on PAID (PD) invoices.
  commissionsSummary: (year) => request(`/commissions/summary?year=${year || new Date().getFullYear()}`),
  myCommissions: (year) => request(`/commissions/me?year=${year || new Date().getFullYear()}`),
  commissionsReconciliation: (year) => request(`/commissions/reconciliation?year=${year || new Date().getFullYear()}`),
  // Commission payments ledger (admin records each payroll run)
  listCommissionPayments: (userId) => request(`/commissions/payments${userId ? '?user_id=' + userId : ''}`),
  createCommissionPayment: (payment) =>
    request('/commissions/payments', { method: 'POST', body: JSON.stringify(payment) }),
  deleteCommissionPayment: (id) =>
    request(`/commissions/payments/${id}`, { method: 'DELETE' }),

  // ===== Workforce (Operations: roster, crafts, DISA, training, allocations) =====
  // Employees (separate from users -- most aren't CRM logins).
  listEmployees: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/workforce/employees${q ? '?' + q : ''}`);
  },
  createEmployee: (e) => request('/workforce/employees', { method: 'POST', body: JSON.stringify(e) }),
  updateEmployee: (id, e) => request(`/workforce/employees/${id}`, { method: 'PUT', body: JSON.stringify(e) }),
  deleteEmployee: (id, { force = false } = {}) => request(`/workforce/employees/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  // Crafts (small lookup, admin-editable).
  listCrafts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/workforce/crafts${q ? '?' + q : ''}`);
  },
  createCraft: (c) => request('/workforce/crafts', { method: 'POST', body: JSON.stringify(c) }),
  updateCraft: (id, c) => request(`/workforce/crafts/${id}`, { method: 'PUT', body: JSON.stringify(c) }),
  deleteCraft: (id) => request(`/workforce/crafts/${id}`, { method: 'DELETE' }),
  // DISA -- one row per employee (LEFT JOIN view), upsert per employee.
  listDisa: () => request('/workforce/disa'),
  saveDisa: (employeeId, d) => request(`/workforce/disa/${employeeId}`, { method: 'PUT', body: JSON.stringify(d) }),
  // DISA report upload (CSV / XLSX export from disaglobal.com). Aggregates
  // each employee's policies + most-recent test date into one row.
  // dry=true returns a preview without writing.
  uploadDisaReport: (file, { dry = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/disa/upload${dry ? '?dry=true' : ''}`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // ISNetworld per-employee training assignments (2026-07-29). Distinct from
  // isnList/isnCreate above, which are the CLIENT-level compliance rows.
  // status: 'open' (default — the ones that stop a man at the gate) | 'all' |
  // an exact status value.
  isnAssignments: ({ status, client, employeeId } = {}) => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (client) q.set('client', client);
    if (employeeId) q.set('employee_id', employeeId);
    const s = q.toString();
    return request(`/workforce/isn/assignments${s ? '?' + s : ''}`);
  },
  isnRequirementGroups: () => request('/workforce/isn/requirement-groups'),
  loadIsnRequirements: (payload) => request('/workforce/isn/load-requirements', {
    method: 'POST', body: JSON.stringify(payload),
  }),
  // Refuse an ISN worker id outright (a junk duplicate record ISN holds).
  blockIsnWorker: (isn_worker_id, reason) => request('/workforce/isn/block', {
    method: 'POST', body: JSON.stringify({ isn_worker_id, reason }),
  }),
  unblockIsnWorker: (isn_worker_id) => request(
    `/workforce/isn/block/${encodeURIComponent(isn_worker_id)}`, { method: 'DELETE' }),
  // Manual upload of the ISN "Online Training Analytics → Export Details"
  // workbook. dry=true previews without writing.
  uploadIsnTraining: (file, { dry = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/isn/upload${dry ? '?dry=true' : ''}`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // Customer sites + requirements mass import (Admin → Import).
  downloadReqsTemplate: async () => {
    const r = await fetch(`${API_BASE}/customer-requirements/import/template.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sss-crm-customer-requirements-template.xlsx';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  importCustomerRequirements: (file, { dry = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/customer-requirements/import${dry ? '?dry=true' : ''}`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // DISA upload template — mirrors the disaglobal.com export format.
  downloadDisaTemplate: async () => {
    const r = await fetch(`${API_BASE}/workforce/disa/template.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sss-crm-disa-template.xlsx';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Roster upload template — every roster category as a column.
  downloadRosterTemplate: async () => {
    const r = await fetch(`${API_BASE}/workforce/roster/template.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sss-crm-roster-template.xlsx';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Roster import (names-only, drag-and-drop on the Roster tab). Reorders
  // "Last, First" -> "First Last" and inserts names not already on the
  // roster. previewRoster (dry) writes nothing; importRoster commits.
  previewRoster: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/roster/upload?dry=true`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  importRoster: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/roster/upload`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // Safety Council training report bulk upload (Training tab).
  uploadTrainingReport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/training/upload`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // Training -- per-cert per-employee, full CRUD.
  listTraining: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/workforce/training${q ? '?' + q : ''}`);
  },
  createTraining: (t) => request('/workforce/training', { method: 'POST', body: JSON.stringify(t) }),
  updateTraining: (id, t) => request(`/workforce/training/${id}`, { method: 'PUT', body: JSON.stringify(t) }),
  deleteTraining: (id) => request(`/workforce/training/${id}`, { method: 'DELETE' }),
  bulkCreateTraining: (records) => request('/workforce/training/bulk', { method: 'POST', body: JSON.stringify({ records }) }),
  // Allocations -- one row per employee per project per craft.
  listAllocations: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/workforce/allocations${q ? '?' + q : ''}`);
  },
  createAllocation: (a) => request('/workforce/allocations', { method: 'POST', body: JSON.stringify(a) }),
  updateAllocation: (id, a) => request(`/workforce/allocations/${id}`, { method: 'PUT', body: JSON.stringify(a) }),
  deleteAllocation: (id) => request(`/workforce/allocations/${id}`, { method: 'DELETE' }),
  // Crew editor (2026-07-30): ops window onto the estimate's Personnel slots.
  getCrewEditor: (projectId) => request(`/workforce/crew-editor/${projectId}`),
  saveCrewEditor: (projectId, personnel) => request(`/workforce/crew-editor/${projectId}`, { method: 'PUT', body: JSON.stringify({ personnel }) }),
  // Layoff / separation — stamps the allocation + emails payroll (2026-07-17).
  separateAllocation: (id, payload) => request(`/workforce/allocations/${id}/separate`, { method: 'POST', body: JSON.stringify(payload) }),
  // Site key contacts (ops job hub → FS PM)
  listSiteContacts: (projectId) => request(`/operations/projects/${projectId}/site-contacts`),
  createSiteContact: (projectId, c) => request(`/operations/projects/${projectId}/site-contacts`, { method: 'POST', body: JSON.stringify(c) }),
  updateSiteContact: (id, c) => request(`/operations/site-contacts/${id}`, { method: 'PUT', body: JSON.stringify(c) }),
  deleteSiteContact: (id) => request(`/operations/site-contacts/${id}`, { method: 'DELETE' }),
  previewSeparationNotice: (id, payload) =>
    fetch(`${API_BASE}/workforce/allocations/${id}/separate/preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // Per-job craft requirements (staffing plan: "Job 808 needs 2 PMs, 4 RTM").
  listJobRequirements: (projectId) => request(`/workforce/requirements${projectId ? '?project_id=' + projectId : ''}`),
  createJobRequirement: (r) => request('/workforce/requirements', { method: 'POST', body: JSON.stringify(r) }),
  updateJobRequirement: (id, r) => request(`/workforce/requirements/${id}`, { method: 'PUT', body: JSON.stringify(r) }),
  deleteJobRequirement: (id) => request(`/workforce/requirements/${id}`, { method: 'DELETE' }),
  // One-time spreadsheet migration: parse the SSS Project Startup Master
  // workbook (LISTS + INPUT sheets) and upsert everything.
  uploadWorkforceSpreadsheet: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const viewAs = getViewAs();
    const url = '/api/workforce/import/upload' + (viewAs ? '?_as=' + viewAs : '');
    return fetch(url, { method: 'POST', headers, body: fd })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        return data;
      });
  },
  commitWorkforceImport: (payload) =>
    request('/workforce/import/commit', { method: 'POST', body: JSON.stringify(payload) }),
  wipeStaffingData: () =>
    request('/workforce/wipe-staffing-data', { method: 'POST' }),

  // Branch Budgets (monthly Base + TA budget/forecast per branch).
  listBranchBudgets: (year) => request(`/branch-budgets${year ? '?year=' + year : ''}`),
  saveBranchBudgetBulk: (branchId, year, months) =>
    request(`/branch-budgets/${branchId}/${year}/bulk`, { method: 'PUT', body: JSON.stringify({ months }) }),
  myBranchBudget: (year) => request(`/branch-budgets/my-branch${year ? '?year=' + year : ''}`),

  // accounts
  listAccounts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/accounts${q ? '?' + q : ''}`);
  },
  getAccount: (name) => request(`/accounts/${encodeURIComponent(name)}`),

  // customers (Rentalman master)
  listCustomers: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/customers${q ? '?' + q : ''}`);
  },
  customerStats: () => request('/customers/stats'),
  customersByName: (name) => request(`/customers/by-name/${encodeURIComponent(name)}`),
  customerInvoices: (params = {}) => request(`/customers/invoices?${new URLSearchParams(params).toString()}`),
  assignCustomer: (id, body) => request(`/customers/${id}/assign`, { method: 'PUT', body: JSON.stringify(body) }),
  bulkAssignCustomers: (body) => request(`/customers/bulk-assign`, { method: 'POST', body: JSON.stringify(body) }),

  // Customer Site Requirements (DISA / Training / Safety Council / Per Diem
  // / Rate / Badging form per customer or per ship-to site).
  getCustomerRequirements: (customerId) => request(`/customer-requirements/${customerId}`),
  // Which course codes anyone holds (Reqs editor warnings): { codes: { CODE: { known, holders } } }.
  knownCertCodes: (codes) => request(`/customer-requirements/known-codes?codes=${encodeURIComponent((codes || []).join(','))}`),
  setCustomerCascade: (customerId, cascade) =>
    request(`/customer-requirements/${customerId}/cascade`, { method: 'PUT', body: JSON.stringify({ reqs_cascade_to_sites: cascade }) }),
  createCustomerSite: (customerId, site) =>
    request(`/customer-requirements/${customerId}/sites`, { method: 'POST', body: JSON.stringify(site) }),
  updateCustomerSite: (siteId, site) =>
    request(`/customer-requirements/sites/${siteId}`, { method: 'PUT', body: JSON.stringify(site) }),
  deleteCustomerSite: (siteId) =>
    request(`/customer-requirements/sites/${siteId}`, { method: 'DELETE' }),
  saveCustomerLevelRequirements: (customerId, payload) =>
    request(`/customer-requirements/${customerId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  saveSiteLevelRequirements: (siteId, payload) =>
    request(`/customer-requirements/site/${siteId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteSiteLevelRequirements: (siteId) =>
    request(`/customer-requirements/site/${siteId}`, { method: 'DELETE' }),
  uploadCustomerBadgingForm: (customerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/customer-requirements/${customerId}/badging-form`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  uploadSiteBadgingForm: (siteId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/customer-requirements/site/${siteId}/badging-form`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // Browser-direct URL for downloading the badging form (auth header
  // attached via window.open won't work; the GET endpoint requires a
  // JWT). For now: render a button that POSTs via fetch and triggers a
  // blob download client-side.
  downloadBadgingForm: async (requirementId, filename) => {
    const r = await fetch(`${API_BASE}/customer-requirements/badging-form/${requirementId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'badging-form';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 100);
  },
  deleteBadgingForm: (requirementId) =>
    request(`/customer-requirements/badging-form/${requirementId}`, { method: 'DELETE' }),
  // Multi-file badging documents (2026-06-11). `files` is a FileList or
  // array. Uploaded sequentially in batches of 10 so a big drop (a whole
  // badging packet) never trips the server's per-request file cap.
  uploadBadgingFiles: async (scope, id, files) => {
    const list = Array.from(files);
    const url = scope === 'customer'
      ? `${API_BASE}/customer-requirements/${id}/badging-files`
      : `${API_BASE}/customer-requirements/site/${id}/badging-files`;
    const out = { ok: true, files: [] };
    for (let i = 0; i < list.length; i += 10) {
      const fd = new FormData();
      for (const f of list.slice(i, i + 10)) fd.append('files', f);
      const r = await fetch(url, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText);
      const j = await r.json();
      out.requirement_id = j.requirement_id;
      out.files.push(...(j.files || []));
    }
    return out;
  },
  downloadBadgingFile: async (fileId, filename) => {
    const r = await fetch(`${API_BASE}/customer-requirements/badging-files/${fileId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'badging-file';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 100);
  },
  deleteBadgingFile: (fileId) =>
    request(`/customer-requirements/badging-files/${fileId}`, { method: 'DELETE' }),
  // Badging form filler (2026-06-11): map fillable PDFs once, then fill +
  // email packets for roster employees.
  badgingFields: (fileId) => request(`/badging/fields/${fileId}`),
  getBadgingMapping: (fileId) => request(`/badging/mappings/${fileId}`),
  extractBadgingMapping: (fileId) => request(`/badging/mappings/${fileId}/extract`, { method: 'POST' }),
  saveBadgingMapping: (fileId, payload) => request(`/badging/mappings/${fileId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  testFillBadgingMapping: async (fileId, fields, filename) => {
    const r = await fetch(`${API_BASE}/badging/mappings/${fileId}/test-fill`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Test fill failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `TEST - ${filename || 'form.pdf'}`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 100);
  },
  badgingFillContext: (fileIds) => request(`/badging/fill-context?file_ids=${fileIds.join(',')}`),
  badgingFill: (payload) => request('/badging/fill', { method: 'POST', body: JSON.stringify(payload) }),
  badgingRuns: (customerId) => request(`/badging/runs?customer_id=${customerId}`),
  deleteBadgingRun: (runId) => request(`/badging/runs/${runId}`, { method: 'DELETE' }),
  // Employee supporting documents (TWIC / DL / certs) — stored on the
  // roster employee, attached to badging submission emails.
  listEmployeeDocuments: (employeeId) => request(`/workforce/employees/${employeeId}/documents`),
  uploadEmployeeDocument: (employeeId, file, label, expiryDate) => {
    const fd = new FormData();
    fd.append('file', file);
    if (label) fd.append('label', label);
    if (expiryDate) fd.append('expiry_date', expiryDate);
    return fetch(`${API_BASE}/workforce/employees/${employeeId}/documents`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  deleteEmployeeDocument: (docId) => request(`/workforce/employees/documents/${docId}`, { method: 'DELETE' }),
  renameEmployeeDocument: (docId, label) =>
    request(`/workforce/employees/documents/${docId}`, { method: 'PUT', body: JSON.stringify({ label }) }),
  downloadEmployeeDocument: async (docId, filename) => {
    const r = await fetch(`${API_BASE}/workforce/employees/documents/${docId}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'document';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 100);
  },
  // Employee badging profile (SSN / DL / TWIC) — extracted from documents
  // by AI, human-reviewed, pre-fills the fill wizard.
  getEmployeeBadgingInfo: (employeeId) => request(`/workforce/employees/${employeeId}/badging-info`),
  saveEmployeeBadgingInfo: (employeeId, payload) =>
    request(`/workforce/employees/${employeeId}/badging-info`, { method: 'PUT', body: JSON.stringify(payload) }),
  extractEmployeeBadgingInfo: (employeeId) =>
    request(`/workforce/employees/${employeeId}/badging-info/extract`, { method: 'POST' }),
  // Home address lives on the employee record (roster data, not a sensitive
  // badging fact) — its own allowOps route so the badging desk can save what
  // it reads off a DL without the admin-only employee PUT.
  saveEmployeeAddress: (employeeId, payload) =>
    request(`/workforce/employees/${employeeId}/address`, { method: 'PUT', body: JSON.stringify(payload) }),
  // Bulk: read the home address off every DL scan already on file, so nobody
  // opens each employee and clicks "read with AI". Fills blanks only.
  addressBackfillStatus: () => request('/workforce/address-backfill/status'),
  runAddressBackfill: (dlOnly = true) =>
    request('/workforce/address-backfill/run', { method: 'POST', body: JSON.stringify({ dl_only: dlOnly }) }),
  // Permanent site badges — one employee can hold several (per customer,
  // or per specific site under a customer).
  listEmployeeBadges: (employeeId) => request(`/workforce/employees/${employeeId}/badges`),
  createEmployeeBadge: (employeeId, payload) =>
    request(`/workforce/employees/${employeeId}/badges`, { method: 'POST', body: JSON.stringify(payload) }),
  deleteEmployeeBadge: (employeeId, badgeId) =>
    request(`/workforce/employees/${employeeId}/badges/${badgeId}`, { method: 'DELETE' }),
  // Bulk badge loader — scoped to one customer (Site Access tab).
  downloadBadgeTemplate: async (customerId) => {
    const r = await fetch(`${API_BASE}/workforce/customers/${customerId}/badges/template.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'site-badges-template.xlsx';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  previewBadgeBulk: (customerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/customers/${customerId}/badges/bulk?dry=true`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  commitBadgeBulk: (customerId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/workforce/customers/${customerId}/badges/bulk`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || r.statusText); return r.json(); });
  },
  // Training records: AI reads the stored Safety Council history / certs /
  // test results; human confirms; import writes employee_training (+ DISA
  // drug test date).
  extractEmployeeTraining: (employeeId) =>
    request(`/workforce/employees/${employeeId}/training/extract`, { method: 'POST' }),
  importEmployeeTraining: (employeeId, payload) =>
    request(`/workforce/employees/${employeeId}/training/import`, { method: 'POST', body: JSON.stringify(payload) }),
  downloadBadgingRunFile: async (fileId, filename) => {
    const r = await fetch(`${API_BASE}/badging/runs/files/${fileId}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'filled-form.pdf';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 100);
  },
  importCustomers: (file, dryRun = false) => {
    const fd = new FormData();
    fd.append('file', file);
    const url = `${API_BASE}/customers/import${dryRun ? '?dry=true' : ''}`;
    return fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },
  deleteCustomer: (id) => request(`/customers/${id}`, { method: 'DELETE' }),

  // notifications
  followUpsToday: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/notifications/follow-ups-today${q ? '?' + q : ''}`);
  },
  flaggedCallLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/notifications/flagged-call-log${q ? '?' + q : ''}`);
  },
  sendReminders: () => request('/notifications/send-reminders', { method: 'POST' }),
  sendWeekly: () => request('/notifications/send-weekly', { method: 'POST' }),
  sendBirthdays: () => request('/notifications/send-birthdays', { method: 'POST' }),
  sendTestEmail: () => request('/notifications/send-test', { method: 'POST' }),
  sendDigestNow: (extraTo = '') => request('/notifications/send-digest', { method: 'POST', body: JSON.stringify({ extra_to: extraTo }) }),
  sendComplianceAlerts: () => request('/notifications/send-compliance', { method: 'POST' }),
  previewComplianceAlerts: () => request('/notifications/send-compliance?dry=true', { method: 'POST' }),
  sendComplianceSms: () => request('/notifications/send-compliance-sms', { method: 'POST' }),
  previewComplianceSms: () => request('/notifications/send-compliance-sms?dry=true', { method: 'POST' }),
  sendSmsTest: (to) => request('/notifications/send-sms-test', { method: 'POST', body: JSON.stringify({ to }) }),
  sendJobReadiness: () => request('/notifications/send-job-readiness', { method: 'POST' }),
  previewJobReadiness: () => request('/notifications/send-job-readiness?dry=true', { method: 'POST' }),
  sendDisaRed: () => request('/notifications/send-disa-red', { method: 'POST' }),
  previewDisaRed: () => request('/notifications/send-disa-red?dry=true', { method: 'POST' }),
  mailStatus: () => request('/notifications/mail-status'),

  // attachments
  listAttachments: (projectId) => request(`/attachments/project/${projectId}`),
  uploadAttachment: (projectId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/attachments/project/${projectId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },
  deleteAttachment: (id) => request(`/attachments/${id}`, { method: 'DELETE' }),
  // Authenticated blob download — streams through Express with the JWT so
  // direct <a href> links (which bypass the auth header) are not needed.
  downloadAttachment: async (id, filename) => {
    const r = await fetch(`${API_BASE}/attachments/${id}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'attachment';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },

  // Activity Log (call log) attachments — same R2 store + download/delete as
  // project attachments, keyed on the entry. Files dual-home onto the linked
  // opportunity server-side (no extra client call).
  listActivityAttachments: (callLogId) => request(`/attachments/call-log/${callLogId}`),
  uploadActivityAttachment: (callLogId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/attachments/call-log/${callLogId}`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },

  // project updates (weekly AM progress posts)
  listProjectUpdates: (projectId) => request(`/project-updates/project/${projectId}`),
  createProjectUpdate: (projectId, body) =>
    request(`/project-updates/project/${projectId}`, { method: 'POST', body: JSON.stringify(body) }),
  updateProjectUpdate: (updateId, body) =>
    request(`/project-updates/${updateId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteProjectUpdate: (updateId) =>
    request(`/project-updates/${updateId}`, { method: 'DELETE' }),
  listProjectUpdateRevisions: (updateId) =>
    request(`/project-updates/${updateId}/revisions`),
  // Admin/leadership: all recent updates grouped-ready (drives digest preview).
  allRecentProjectUpdates: (days = 7) => request(`/project-updates/all-recent?days=${days}`),

  // What's New (product changelog) — { entries, unread, seen_at }
  whatsNew: () => request('/whats-new'),
  markWhatsNewSeen: () => request('/whats-new/seen', { method: 'POST' }),

  // estimate revisions
  listEstimateRevisions: (estimateId) => request(`/estimates/${estimateId}/revisions`),
  getEstimateRevisionSnapshot: (estimateId, revId) =>
    request(`/estimates/${estimateId}/revisions/${revId}/snapshot`),

  // request revisions
  listRequestRevisions: (requestId) => request(`/requests/${requestId}/revisions`),
  getRequestRevisionSnapshot: (requestId, revId) =>
    request(`/requests/${requestId}/revisions/${revId}/snapshot`),

  // imports
  // Templates are admin-gated server-side (2026-07-26), so they can no longer
  // be plain <a href> anchors — a bare browser GET sends no Authorization
  // header. Same authenticated blob download the DISA / roster / badges /
  // customer-requirements templates already use.
  downloadImportTemplate: async (kind) => {
    const FILES = {
      projects: ['template/projects', 'sss-crm-projects-template.xlsx'],
      'call-log': ['template/call-log', 'sss-crm-call-log-template.xlsx'],
      users: ['template/users', 'sss-crm-users-template.xlsx'],
    };
    const entry = FILES[kind];
    if (!entry) throw new Error(`Unknown import template: ${kind}`);
    const [pathPart, filename] = entry;
    const r = await fetch(`${API_BASE}/import/${pathPart}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  importUsers: (file, tempPassword) => {
    const fd = new FormData();
    fd.append('file', file);
    // Password in FormData body, not URL query param, so it stays out of
    // Railway access logs and proxy request logs.
    if (tempPassword) fd.append('password', tempPassword);
    return fetch(`${API_BASE}/import/users`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },
  importProjects: (file, defaultStatus) => {
    const fd = new FormData();
    fd.append('file', file);
    const url = `${API_BASE}/import/projects${defaultStatus ? '?default_status=' + encodeURIComponent(defaultStatus) : ''}`;
    return fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },
  importCallLog: (file, repId) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/import/call-log?rep_id=${repId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd
    }).then(async r => {
      const text = await r.text();
      const data = text ? JSON.parse(text) : null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      return data;
    });
  },

  // ---- Price history (what we quoted before, and what won) -----------------
  priceHintsForEstimate: (id) => request(`/price-history/for-estimate/${id}`),
  priceHistoryDetail: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request(`/price-history/detail?${q}`);
  },
  priceBook: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request(`/price-history/book${q ? '?' + q : ''}`);
  },

  // ---- Estimating module --------------------------------------------------
  listEstimates: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/estimates${q ? '?' + q : ''}`);
  },
  getEstimate: (id) => request(`/estimates/${id}`),
  createEstimate: (body) => request('/estimates', { method: 'POST', body: JSON.stringify(body) }),
  updateEstimate: (id, body) => request(`/estimates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  relinkEstimate: (id, projectId) =>
    request(`/estimates/${id}/project`, { method: 'PUT', body: JSON.stringify({ project_id: projectId }) }),
  // Mark/unmark a Draft estimate as a change order of another estimate on the
  // same opportunity (pass null to unlink).
  setEstimateChangeOrder: (id, change_order_of_estimate_id) =>
    request(`/estimates/${id}/change-order`, { method: 'PUT', body: JSON.stringify({ change_order_of_estimate_id }) }),
  deleteEstimate: (id, deleteOpportunity = false) =>
    request(`/estimates/${id}${deleteOpportunity ? '?delete_opportunity=true' : ''}`, { method: 'DELETE' }),
  // What would happen to the linked opportunity if this estimate were deleted.
  estimateOpportunityUsage: (id) => request(`/estimates/${id}/opportunity-usage`),
  updateEstimateMarkup: (id, lines) => request(`/estimates/${id}/markup`, { method: 'PUT', body: JSON.stringify({ lines }) }),
  addLaborLine: (id, body) => request(`/estimates/${id}/labor`, { method: 'POST', body: JSON.stringify(body) }),
  updateLaborLine: (lineId, body) => request(`/estimates/labor/${lineId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteLaborLine: (lineId) => request(`/estimates/labor/${lineId}`, { method: 'DELETE' }),
  updateLaborPhases: (lineId, phases) => request(`/estimates/labor/${lineId}/phases`, { method: 'PUT', body: JSON.stringify({ phases }) }),
  updateEquipmentPhases: (lineId, phases) => request(`/estimates/equipment/${lineId}/phases`, { method: 'PUT', body: JSON.stringify({ phases }) }),
  bulkApplyLabor: (id, body) => request(`/estimates/${id}/labor/bulk-apply`, { method: 'POST', body: JSON.stringify(body) }),
  addEquipmentLine: (id, body) => request(`/estimates/${id}/equipment`, { method: 'POST', body: JSON.stringify(body) }),
  updateEquipmentLine: (lineId, body) => request(`/estimates/equipment/${lineId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEquipmentLine: (lineId) => request(`/estimates/equipment/${lineId}`, { method: 'DELETE' }),
  applyEquipmentDates: (id, body) => request(`/estimates/${id}/equipment/apply-dates`, { method: 'POST', body: JSON.stringify(body) }),
  addConsumableLine: (id, body) => request(`/estimates/${id}/consumables`, { method: 'POST', body: JSON.stringify(body) }),
  updateConsumableLine: (lineId, body) => request(`/estimates/consumables/${lineId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteConsumableLine: (lineId) => request(`/estimates/consumables/${lineId}`, { method: 'DELETE' }),
  applyConsumableMarkup: (id, markup) => request(`/estimates/${id}/consumables/apply-markup`, { method: 'POST', body: JSON.stringify({ markup }) }),
  listEquipmentCatalog: (q = '') => request(`/estimates/catalog${q ? '?q=' + encodeURIComponent(q) : ''}`),
  // Workbook round-trip: one .xlsx, 4 sheets (Labor / Equipment / Third-Party /
  // Consumables). Export current lines, edit in Excel, import back (preview first).
  exportEstimateWorkbook: async (id) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/workbook.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `estimate-${id}-lines.xlsx`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Consolidated BILL-SIDE summary (Dashboard + Manpower + Equipment + Daily
  // Schedule) — the "puts everything together" export. No cost/margin.
  exportBilledWorkbook: async (id) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/billed-workbook.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `estimate-${id}-billed-summary.xlsx`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  exportPersonnelGantt: async (id) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/personnel-gantt.xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `estimate-${id}-personnel-gantt.xlsx`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  updatePersonnel: (id, personnel) => request(`/estimates/${id}/personnel`, { method: 'PUT', body: JSON.stringify({ personnel }) }),
  // Soft-reserve this estimate's equipment in the Inventory app before the
  // deal is finalized (does not pull/stage; converts to a pull list on win).
  reserveEstimateEquipment: (id) => request(`/estimates/${id}/reserve`, { method: 'POST' }),
  sendEstimateStatusEmail: (id, opts) => request(`/estimates/${id}/send-email${opts && opts.preview ? '?preview=true' : ''}`, { method: 'POST' }),
  // Push this estimate's equipment to Inventory as a REAL pull list (bid won,
  // not yet flipped to Won) — pullable immediately; later pushes supersede.
  pushEstimatePullList: (id) => request(`/estimates/${id}/pull`, { method: 'POST' }),
  exportOrgChart: async (id) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/org-chart.pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `estimate-${id}-org-chart.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Budgetary estimate PDF (server-rendered, the same document the email sends
  // and files on the opportunity). Downloads under the server's filename.
  downloadBudgetaryPdf: async (id, notes) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/budgetary.pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ notes: notes || '' }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Budgetary PDF failed');
    const cd = r.headers.get('Content-Disposition') || '';
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    const name = m ? decodeURIComponent(m[1]) : `budgetary-EST-${id}.pdf`;
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Quote PDF (server-rendered, the same document the AM email carries and the
  // opportunity files). The Quote tab's Print / PDF button.
  downloadQuotePdf: async (id) => {
    const r = await fetch(`${API_BASE}/quotes/${id}/pdf`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Quote PDF failed');
    const cd = r.headers.get('Content-Disposition') || '';
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    const name = m ? decodeURIComponent(m[1]) : `quote-${id}.pdf`;
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  getBudgetaryRecipients: (id) => request(`/estimates/${id}/budgetary/recipients`),
  emailBudgetary: (id, body) => request(`/estimates/${id}/budgetary/email`, { method: 'POST', body: JSON.stringify(body) }),
  // Consolidated request -> estimate -> quote -> PO audit timeline for the job.
  getEstimateAuditTrail: (id) => request(`/estimates/${id}/audit-trail`),
  exportAuditTrail: async (id) => {
    const r = await fetch(`${API_BASE}/estimates/${id}/audit-trail.pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `estimate-${id}-audit-trail.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  previewEstimateWorkbook: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/estimates/${id}/workbook?dry=true`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; });
  },
  importEstimateWorkbook: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/estimates/${id}/workbook`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; });
  },

  // ---- Sales Requests (Phase 2) -------------------------------------------
  getRequestCatalog:   ()       => request('/requests/catalog'),
  requestFromEstimate: (body)   => request('/requests/from-estimate', { method:'POST', body: JSON.stringify(body) }),
  // Equipment catalog search on the requests router (AM-accessible — the
  // /estimates/catalog one is admin/leadership only).
  listRequestEquipmentCatalog: (q = '') => request(`/requests/equipment-catalog${q ? '?q=' + encodeURIComponent(q) : ''}`),
  listRequests:  (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/requests${q ? '?' + q : ''}`); },
  getRequest:    (id)           => request(`/requests/${id}`),
  createRequest: (body)         => request('/requests', { method:'POST', body: JSON.stringify(body) }),
  updateRequest: (id, body)     => request(`/requests/${id}`, { method:'PUT', body: JSON.stringify(body) }),
  saveRequestAnswers: (id, answers) => request(`/requests/${id}/answers`, { method:'PUT', body: JSON.stringify({ answers }) }),
  submitRequest: (id)           => request(`/requests/${id}/submit`, { method:'POST' }),
  deleteRequest: (id) => request(`/requests/${id}`, { method:'DELETE' }),
  uploadRequestMsa: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/requests/${id}/msa`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText); return r.json(); });
  },
  downloadRequestMsa: async (id, filename) => {
    const r = await fetch(`${API_BASE}/requests/${id}/msa`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || 'Download failed');
    const blob = await r.blob(); const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'msa'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  deleteRequestMsa: (id) => request(`/requests/${id}/msa`, { method:'DELETE' }),
  createEstimateFromRequest: (id) => request(`/requests/${id}/create-estimate`, { method:'POST' }),
  reassignRequest:   (id, user_id) => request(`/requests/${id}/reassign`, { method:'PUT', body: JSON.stringify({ user_id }) }),
  returnRequest:     (id, note) => request(`/requests/${id}/return`, { method:'POST', body: JSON.stringify({ note }) }),
  addRequestLabor:   (id, body) => request(`/requests/${id}/labor`, { method:'POST', body: JSON.stringify(body) }),
  updateRequestLabor:(lineId, body) => request(`/requests/labor/${lineId}`, { method:'PUT', body: JSON.stringify(body) }),
  deleteRequestLabor:(lineId)   => request(`/requests/labor/${lineId}`, { method:'DELETE' }),
  updateRequestLaborPhases: (lineId, phases) => request(`/requests/labor/${lineId}/phases`, { method:'PUT', body: JSON.stringify({ phases }) }),
  addRequestEquipment:   (id, body) => request(`/requests/${id}/equipment`, { method:'POST', body: JSON.stringify(body) }),
  updateRequestEquipment:(lineId, body) => request(`/requests/equipment/${lineId}`, { method:'PUT', body: JSON.stringify(body) }),
  deleteRequestEquipment:(lineId) => request(`/requests/equipment/${lineId}`, { method:'DELETE' }),
  applyRequestEquipmentDates: (id, body) => request(`/requests/${id}/equipment/apply-dates`, { method:'POST', body: JSON.stringify(body) }),
  addRequestConsumable:   (id, body) => request(`/requests/${id}/consumables`, { method:'POST', body: JSON.stringify(body) }),
  updateRequestConsumable:(lineId, body) => request(`/requests/consumables/${lineId}`, { method:'PUT', body: JSON.stringify(body) }),
  deleteRequestConsumable:(lineId) => request(`/requests/consumables/${lineId}`, { method:'DELETE' }),
  applyRequestConsumableMarkup: (id, markup) => request(`/requests/${id}/consumables/apply-markup`, { method:'POST', body: JSON.stringify({ markup }) }),

  // ---- CSVM vendor proposal (project info + scope + CSVM sections) ---------
  exportCsvmProposal: async (id, reqNo) => {
    const r = await fetch(`${API_BASE}/requests/${id}/csvm-proposal.pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${reqNo || `request-${id}`}-CSVM-Proposal.pdf`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  emailCsvmProposal: (id, to) => request(`/requests/${id}/csvm-proposal/email`, { method:'POST', body: JSON.stringify({ to }) }),
  // Vendor response: SSS PO # and/or which request file is the vendor's quote
  // (upload via uploadRequestFile first, then tag its id here).
  updateRequestCsvmVendor: (id, body) => request(`/requests/${id}/csvm-vendor`, { method:'PUT', body: JSON.stringify(body) }),

  // ---- Request revisions / history / customer files ------------------------
  reviseRequest: (id) => request(`/requests/${id}/revise`, { method:'POST' }),
  getRequestHistory: (id) => request(`/requests/${id}/history`),
  listRequestFiles: (id) => request(`/requests/${id}/files`),
  uploadRequestFile: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/requests/${id}/files`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText); return r.json(); });
  },
  // Fill the questionnaire from an old estimate workbook's "Project
  // Information" sheet (deterministic question-text match, no AI).
  // dry=true previews; overwrite=true replaces existing answers.
  importRequestAnswers: (id, file, { dry, overwrite, replaceLines } = {}) => {
    const fd = new FormData(); fd.append('file', file);
    if (overwrite) fd.append('overwrite', 'true');
    if (replaceLines) fd.append('replace_lines', 'true');
    return fetch(`${API_BASE}/requests/${id}/import-answers${dry ? '?dry=true' : ''}`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText); return r.json(); });
  },
  downloadRequestFile: async (id, fileId, filename) => {
    const r = await fetch(`${API_BASE}/requests/${id}/files/${fileId}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || 'Download failed');
    const blob = await r.blob(); const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'file'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  deleteRequestFile: (id, fileId) => request(`/requests/${id}/files/${fileId}`, { method:'DELETE' }),

  // ---- Vendor quote files (Third-Party rental lines) -------------------------
  listEquipLineFiles: (lineId) => request(`/estimates/equipment/${lineId}/files`),
  uploadEquipLineFile: (lineId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/estimates/equipment/${lineId}/files`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText); return r.json(); });
  },
  downloadEquipLineFile: async (lineId, fileId, filename) => {
    const r = await fetch(`${API_BASE}/estimates/equipment/${lineId}/files/${fileId}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || 'Download failed');
    const blob = await r.blob(); const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'file'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  deleteEquipLineFile: (lineId, fileId) => request(`/estimates/equipment/${lineId}/files/${fileId}`, { method:'DELETE' }),
  emailEquipmentVendor: (lineId, to) => request(`/estimates/equipment/${lineId}/email-vendor`, { method:'POST', body: JSON.stringify({ to }) }),

  // ---- Vendor quote files (Consumable / Mobilization lines) -----------------
  listConsLineFiles: (lineId) => request(`/estimates/consumables/${lineId}/files`),
  uploadConsLineFile: (lineId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/estimates/consumables/${lineId}/files`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || r.statusText); return r.json(); });
  },
  downloadConsLineFile: async (lineId, fileId, filename) => {
    const r = await fetch(`${API_BASE}/estimates/consumables/${lineId}/files/${fileId}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || 'Download failed');
    const blob = await r.blob(); const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'file'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  deleteConsLineFile: (lineId, fileId) => request(`/estimates/consumables/${lineId}/files/${fileId}`, { method:'DELETE' }),
  emailConsumableVendor: (lineId, to) => request(`/estimates/consumables/${lineId}/email-vendor`, { method:'POST', body: JSON.stringify({ to }) }),

  // ---- Estimate revisions / history -----------------------------------------
  reviseEstimate: (id) => request(`/estimates/${id}/revise`, { method:'POST' }),
  // Copy an estimate into a brand-new estimate (its own chain, Draft), on any
  // opportunity, re-dated to a new window. Not a revision: the source is
  // untouched and both stay live.
  duplicateEstimate: (id, body) =>
    request(`/estimates/${id}/duplicate`, { method: 'POST', body: JSON.stringify(body || {}) }),
  getEstimateHistory: (id) => request(`/estimates/${id}/history`),

  // ---- Quotes ----------------------------------------------------------------
  listQuotes: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/quotes${q ? '?' + q : ''}`);
  },
  getQuote: (id) => request(`/quotes/${id}`),
  createQuote: (body) => request('/quotes', { method:'POST', body: JSON.stringify(body) }),
  updateQuote: (id, body) => request(`/quotes/${id}`, { method:'PUT', body: JSON.stringify(body) }),
  reviseQuote: (id, body = {}) => request(`/quotes/${id}/revise`, { method:'POST', body: JSON.stringify(body) }),
  deleteQuote: (id) => request(`/quotes/${id}`, { method:'DELETE' }),
  getQuoteHistory: (id) => request(`/quotes/${id}/history`),
  // Email the quote PDF to the AM who owns the deal -> { sent, to, reason }.
  // opts.preview resolves the recipient WITHOUT sending so the UI can confirm
  // where it's going first -> { preview, to, rep_name, customer, via_notify_list, cc, reason }
  emailQuote: (id, opts) => request(`/quotes/${id}/email${opts && opts.preview ? '?preview=true' : ''}`, { method:'POST' }),
  // Manual backfill: push an Approved estimate's total + current quote PDF to
  // the linked opportunity (the auto-sync only fires at approval time).
  // -> { value_updated, total, quote_attached, quote_reason }
  pushEstimateToOpportunity: (estimateId) =>
    request('/quotes/push-to-opportunity', { method:'POST', body: JSON.stringify({ estimate_id: estimateId }) }),
  resyncCrewPlan: (id) => request(`/estimates/${id}/seed-requirements`, { method: 'POST' }),
  estimateMobMiles: (id) => request(`/estimates/${id}/mob-miles`, { method: 'POST' }),

  // ---- MSA rate sheets --------------------------------------------------------
  msaStatus: () => request('/msa/status'),
  listMsaSheets: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/msa/sheets${q ? '?' + q : ''}`);
  },
  getMsaSheet: (id) => request(`/msa/sheets/${id}`),
  getMsaSheetForEstimate: (estimateId) => request(`/msa/sheets/for-estimate/${estimateId}`),
  getMsaSheetForRequest: (requestId) => request(`/msa/sheets/for-request/${requestId}`),
  extractMsaRates: (requestId) => request('/msa/extract', { method:'POST', body: JSON.stringify({ request_id: requestId }) }),
  updateMsaSheet: (id, body) => request(`/msa/sheets/${id}`, { method:'PUT', body: JSON.stringify(body) }),
  addMsaLine: (sheetId, body) => request(`/msa/sheets/${sheetId}/lines`, { method:'POST', body: JSON.stringify(body) }),
  updateMsaLine: (lineId, body) => request(`/msa/lines/${lineId}`, { method:'PUT', body: JSON.stringify(body) }),
  deleteMsaLine: (lineId) => request(`/msa/lines/${lineId}`, { method:'DELETE' }),
  deleteMsaSheet: (id) => request(`/msa/sheets/${id}`, { method:'DELETE' }),

  // Sales Library — shared collateral repository (decks / literature / platforms).
  listSalesLibrary: ({ q = '', category = '' } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (category) params.set('category', category);
    const qs = params.toString();
    return request(`/sales-library${qs ? '?' + qs : ''}`);
  },
  salesLibraryCategories: () => request('/sales-library/categories'),
  createSalesLibraryCategory: (name) => request('/sales-library/categories', { method: 'POST', body: JSON.stringify({ name }) }),
  deleteSalesLibraryCategory: (id) => request(`/sales-library/categories/${id}`, { method: 'DELETE' }),
  uploadSalesLibrary: ({ file, title, description, category }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (description) fd.append('description', description);
    if (category) fd.append('category', category);
    return fetch(`${API_BASE}/sales-library`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`); return d; });
  },
  updateSalesLibrary: (id, body) => request(`/sales-library/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSalesLibrary: (id) => request(`/sales-library/${id}`, { method: 'DELETE' }),
  downloadSalesLibrary: async (id, filename) => {
    const r = await fetch(`${API_BASE}/sales-library/${id}/download`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'file';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },

  // RM Quotes tab: drop RM sales quote PDFs / the SSS Excel template, review,
  // download the SSS-branded PDF, file it on the opportunity.
  listRmQuotes: () => request('/rm-quotes'),
  getRmQuote: (id) => request(`/rm-quotes/${id}`),
  updateRmQuote: (id, body) => request(`/rm-quotes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  fileRmQuote: (id) => request(`/rm-quotes/${id}/file`, { method: 'POST' }),
  createRmQuoteOpportunity: (id, body) => request(`/rm-quotes/${id}/opportunity`, { method: 'POST', body: JSON.stringify(body) }),
  issueRmQuote: (id) => request(`/rm-quotes/${id}/issue`, { method: 'POST' }),
  sendRmQuoteToEstimating: (id) => request(`/rm-quotes/${id}/estimating`, { method: 'POST' }),
  deleteRmQuote: (id) => request(`/rm-quotes/${id}`, { method: 'DELETE' }),
  // Email the branded PDF to the customer (you are CC'd); history + resend.
  emailRmQuote: (id, body) => request(`/rm-quotes/${id}/email`, { method: 'POST', body: JSON.stringify(body) }),
  listRmQuoteEmails: (id) => request(`/rm-quotes/${id}/emails`),
  resendRmQuoteEmail: (id, emailId) => request(`/rm-quotes/${id}/emails/${emailId}/resend`, { method: 'POST' }),
  uploadRmQuotes: (files) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    return fetch(`${API_BASE}/rm-quotes`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    }).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      return d;
    });
  },
  // path: 'template' | '<id>/pdf' | '<id>/source'
  downloadRmQuoteFile: async (path, filename) => {
    const r = await fetch(`${API_BASE}/rm-quotes/${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Download failed');
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'file';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
};

export const setToken = (token) => localStorage.setItem('sss_crm_token', token);
export const clearToken = () => localStorage.removeItem('sss_crm_token');
